import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ReactNode } from "react";

export type MetaTone = "primary" | "secondary" | "info" | "success" | "warning" | "error" | "neutral";

export interface MetaPillProps {
  title: string;
  value: ReactNode;
  icon?: ReactNode;
  tone?: MetaTone;
  /** Trailing control, e.g. a delete button. */
  action?: ReactNode;
  /** One muted line under the value. */
  hint?: string;
}

/** A toned label/value tile for a workspace header: icon, small coloured
 * caption, bold value. The tone tints the tile so the strip reads at a glance. */
export default function MetaPill({ title, value, icon, tone = "neutral", action, hint }: MetaPillProps) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const main = tone === "neutral" ? theme.palette.text.secondary : theme.palette[tone].main;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        pl: icon ? 1 : 1.25,
        pr: action ? 0.5 : 1.25,
        py: 0.6,
        minHeight: 48,
        borderRadius: 2,
        bgcolor: alpha(main, dark ? 0.16 : 0.07),
        border: 1,
        borderColor: alpha(main, dark ? 0.45 : 0.28),
      }}
    >
      {icon ? (
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: alpha(main, dark ? 0.28 : 0.14),
            color: main,
            "& svg": { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="overline"
          sx={{
            display: "block",
            lineHeight: 1.1,
            letterSpacing: 0.08,
            fontWeight: 700,
            fontSize: 10,
            color: main,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ lineHeight: 1.3, color: "text.primary", overflowWrap: "anywhere" }}
        >
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
            {hint}
          </Typography>
        ) : null}
      </Box>
      {action ? <Box sx={{ ml: 0.25, display: "flex", alignItems: "center" }}>{action}</Box> : null}
    </Box>
  );
}
